import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router";
import { useAuth, useCan } from "../auth/AuthProvider";
import { Icon } from "../components/icons";
import { Pagination } from "../components/Pagination";
import {
  Alert,
  Card,
  CardHeader,
  LoadingBlock,
  Mono,
  PageHeader,
  StatusBadge,
  Table,
  Td,
  Th,
  buttonStyles,
} from "../components/ui";
import { QuotaMeter } from "../components/Quota";
import { regionLabel } from "../lib/regions";
import { useTunnels } from "../lib/queries";
import { tunnelStatus } from "./tunnel/status";

/** Cents to a plain dollar figure. Every tunnel is the same flat add-on price. */
const monthly = (cents: number) => `$${(cents / 100).toFixed(2)}`;

/*
 * A page of rows rather than a page of cards.
 *
 * The list used to render each connection as a panel carrying its own
 * addresses, its own setup instructions and its own delete — which meant every
 * row fetched the detail and asked the engine whether it was carrying traffic.
 * Ten connections were twenty requests to draw a list, and the screen was a
 * long scroll of repeated instructions. Twenty fits comfortably now.
 */
const PER_PAGE = 20;

/**
 * Every tunnel the organization owns, and nothing about any one of them that
 * costs a round trip.
 *
 * Each row answers the questions a list is for — is it up, what do apps
 * connect to, where is it, what does it cost — and the detail page behind it
 * carries the addresses, the setup half that happens on the customer's own
 * network, and the delete.
 */
export function TunnelsPage() {
  const { t } = useTranslation("tunnels");
  const { currentOrg } = useAuth();
  const orgId = currentOrg?.id;
  const canManage = useCan("admin");

  const [page, setPage] = useState(1);
  const tunnels = useTunnels(orgId, { limit: PER_PAGE, offset: (page - 1) * PER_PAGE });

  // Left behind by a delete on the detail page, which has nowhere to show the
  // outcome of the thing that navigated away from it.
  const location = useLocation();
  const notice = (location.state as { notice?: string } | null)?.notice ?? null;

  const rows = tunnels.data?.data ?? [];
  const total = tunnels.data?.meta.total ?? 0;
  // Both come from the response and neither has a real default: the rows they
  // price only render once `tunnels.data` exists, so a hard-coded five dollars
  // here would be an unreachable line that still claims to know the price.
  const unitCents = tunnels.data?.meta.monthlyCents ?? 0;
  const monthlyTotal = tunnels.data?.meta.monthlyCentsTotal ?? 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("title")}
        description={t("description")}
        action={
          canManage && total > 0 ? (
            <div className="flex flex-col items-end gap-1.5">
              <Link to="/tunnels/new" className={buttonStyles("primary")}>
                <Icon name="plus" className="size-3.5" />
                {t("create")}
              </Link>
              <QuotaMeter orgId={currentOrg?.id} resource="tunnels" />
            </div>
          ) : undefined
        }
      />

      {notice && <Alert tone="success">{notice}</Alert>}
      {tunnels.error && <Alert>{tunnels.error.message}</Alert>}

      {tunnels.isPending ? (
        <Card>
          <LoadingBlock />
        </Card>
      ) : total === 0 ? (
        <GettingStarted canManage={canManage} unitCents={unitCents} />
      ) : (
        <Card>
          <Table
            head={
              <>
                <Th>{t("columns.tunnel")}</Th>
                <Th>{t("columns.status")}</Th>
                <Th priority="md">{t("columns.hostname")}</Th>
                <Th priority="sm">{t("columns.addresses")}</Th>
                <Th priority="lg">{t("columns.region")}</Th>
                <Th className="text-right">{t("columns.cost")}</Th>
              </>
            }
          >
            {rows.map((tunnel) => {
              const status = tunnelStatus(tunnel.state);
              return (
                <tr key={tunnel.id} className="transition-colors hover:bg-raised/50">
                  <Td>
                    <Link
                      to={`/tunnels/${tunnel.id}`}
                      className="font-medium text-fg hover:underline"
                    >
                      {tunnel.name}
                    </Link>
                    {/* The region, for the widths where its own column is
                        gone. A tunnel is reachable from one region's workloads
                        and no other, so it is never a detail worth dropping. */}
                    <div className="mt-0.5 text-[11px] text-fg-subtle lg:hidden">
                      {regionLabel(tunnel.region) ?? tunnel.region ?? "—"}
                    </div>
                  </Td>
                  <Td>
                    {/* The same badge the detail page wears, so a row and the
                        page behind it never disagree about how a state looks. */}
                    <StatusBadge
                      tone={status.tone}
                      label={t(`state.${status.key}.label`)}
                      title={t(`state.${status.key}.hint`)}
                      stale={tunnel.stale}
                    />
                  </Td>
                  <Td priority="md">
                    {tunnel.internalHost ? (
                      <Mono className="text-[12px] text-fg-muted">{tunnel.internalHost}</Mono>
                    ) : (
                      /* No container, so no hostname — and a blank cell would
                         read as one that simply has not loaded. */
                      <span className="text-fg-subtle">{t("notCreated")}</span>
                    )}
                  </Td>
                  <Td priority="sm" className="tabular-nums text-fg-muted">
                    {tunnel.targetCount > 0 ? (
                      tunnel.targetCount
                    ) : (
                      /* Nothing is reachable through a tunnel with no
                         addresses, which is the one row state somebody has to
                         act on and would otherwise read as a zero. */
                      <span className="text-fg-subtle">{t("noneYet")}</span>
                    )}
                  </Td>
                  <Td priority="lg" className="text-fg-muted">
                    {regionLabel(tunnel.region) ?? tunnel.region ?? "—"}
                  </Td>
                  <Td className="text-right tabular-nums text-fg-muted">
                    {/* On the meter's own rule, not on the state column: a
                        tunnel is charged for existing on the engine, so one
                        that never reached it is free and says so. */}
                    {tunnel.provisioned ? (
                      t("perMonth", { amount: monthly(unitCents) })
                    ) : (
                      <span className="text-fg-subtle">{t("notBilled")}</span>
                    )}
                  </Td>
                </tr>
              );
            })}
          </Table>

          {/*
            The count and what it costs, on the page the connections are on.
            A stopped tunnel is inside this figure, which is the reason to show
            it: stopping one does not stop its charge, only deleting it does.
          */}
          <div className="flex flex-wrap items-baseline justify-between gap-2 border-t border-line px-4 py-3 text-[13px]">
            <p className="text-fg-muted">{t("total", { count: total })}</p>
            <p className="text-fg-muted">
              <Trans
                ns="tunnels"
                i18nKey="monthly"
                values={{ amount: monthly(monthlyTotal) }}
                components={{ amount: <span className="tabular-nums text-fg" /> }}
              />
            </p>
          </div>

          {/*
            Renders nothing on a single page, so an organization with two
            connections never sees a control it does not need.
          */}
          <Pagination total={total} perPage={PER_PAGE} page={page} onChange={setPage} />
        </Card>
      )}
    </div>
  );
}

/**
 * The empty state, which is the only place the shape of this feature gets
 * explained.
 *
 * It leads with the reason not to use it. Most people asking for "VPC support"
 * have a service with a public endpoint and a firewall, and for them the
 * answer is one allowlist entry and no moving parts — sending them to a paid
 * tunnel instead would be selling something they do not need.
 *
 * It says "service" rather than "database" throughout, because the tunnel
 * forwards TCP and does not care what is listening: a database is the common
 * case and never was the limit. What it does care about is TCP, which is the
 * one real constraint and is stated here rather than discovered by somebody
 * pointing a tunnel at a UDP service and watching nothing happen.
 */
function GettingStarted({
  canManage,
  /**
   * From the response, like every other price on this page.
   *
   * Safe to render unguarded: this card is only reached once the list has
   * loaded, so the figure is the add-on's real one rather than a zero
   * standing in for "not asked yet".
   */
  unitCents,
}: {
  canManage: boolean;
  unitCents: number;
}) {
  const { t } = useTranslation("tunnels");
  const steps: { title: string; detail: string }[] = t("gettingStarted.steps", {
    returnObjects: true,
  });

  return (
    <Card>
      <CardHeader
        title={t("gettingStarted.title")}
        description={t("gettingStarted.description")}
      />

      {/*
        The cheaper answer first. Most people asking for this have a service
        with a public endpoint and a firewall, and for them it is one allowlist
        entry and nothing to run.
      */}
      <p className="border-b border-line px-5 py-4 text-sm leading-6 text-fg-muted">
        {t("gettingStarted.cheaper")}
      </p>

      <ol className="grid gap-px border-b border-line bg-line md:grid-cols-3">
        {steps.map((step, i) => (
          <li key={step.title} className="min-w-0 bg-surface p-5">
            <p className="micro text-fg-subtle">{t("gettingStarted.step", { number: i + 1 })}</p>
            <p className="mt-2 text-sm font-medium text-fg">{step.title}</p>
            <p className="mt-1 text-sm leading-6 text-fg-muted">{step.detail}</p>
          </li>
        ))}
      </ol>

      <div className="flex flex-wrap items-center justify-between gap-4 px-5 py-4">
        <p className="text-xs text-fg-subtle">
          {t("gettingStarted.price", { amount: monthly(unitCents) })}
        </p>
        {canManage && (
          <Link to="/tunnels/new" className={buttonStyles("primary")}>
            {t("create")}
          </Link>
        )}
      </div>
    </Card>
  );
}
